import { useState } from 'react';
import {
  Shield, Database, Cloud, Globe, Zap, Settings, 
  BarChart3, Users, Package, Upload, Monitor,
  ArrowRight, Star, CheckCircle, Layers, Server
} from 'lucide-react';

const UnifiedSuperAdmin = () => {
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const adminPanels = [
    {
      id: 'supabase-admin',
      title: 'Supabase Super Admin',
      description: 'Complete Supabase storage management with real-time database operations',
      icon: Database,
      color: 'green',
      gradient: 'from-green-500 to-emerald-600',
      route: '/supabase-super-admin',
      features: [
        'Real-time database operations',
        'Supabase storage management', 
        'Product metadata editing',
        'Bulk image operations',
        'Database analytics'
      ],
      benefits: [
        'Real-time sync across all devices',
        'PostgreSQL database reliability',
        'Advanced query capabilities',
        'Built-in authentication',
        'Automatic backups'
      ],
      stats: {
        storage: 'Unlimited',
        performance: 'Real-time',
        uptime: '99.9%',
        locations: 'Global'
      }
    },
    {
      id: 'vercel-admin',
      title: 'Vercel Super Admin',
      description: 'Global CDN management with 100+ edge locations for lightning-fast delivery',
      icon: Cloud,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-600',
      route: '/vercel-super-admin',
      features: [
        'Global CDN with 100+ edge locations',
        'Zero cold starts & instant serving',
        'Automatic image optimization',
        'Enterprise security & 99.9% uptime',
        'Real-time storage analytics'
      ],
      benefits: [
        'Lightning-fast global delivery',
        'Automatic image optimization',
        'Enterprise-grade security',
        'Cost-effective pricing',
        'Zero configuration required'
      ],
      stats: {
        storage: '5GB Free',
        performance: 'Zero Cold Start',
        uptime: '99.9%',
        locations: '100+ Edge'
      }
    },
    {
      id: 'hybrid-admin',
      title: 'Hybrid Super Admin',
      description: 'Best of both worlds - combine Supabase reliability with Vercel performance',
      icon: Layers,
      color: 'purple',
      gradient: 'from-purple-500 to-indigo-600',
      route: '/hybrid-super-admin',
      features: [
        'Dual storage system management',
        'Choose between Supabase & Vercel',
        'Unified interface for both platforms',
        'Smart storage recommendations',
        'Cross-platform synchronization'
      ],
      benefits: [
        'Flexibility to choose storage',
        'Redundancy and backup options',
        'Cost optimization strategies',
        'Performance comparison tools',
        'Seamless migration between platforms'
      ],
      stats: {
        storage: 'Dual System',
        performance: 'Optimized',
        uptime: '99.9%+',
        locations: 'Multi-CDN'
      }
    }
  ];

  const handleAdminSelect = (admin) => {
    setSelectedAdmin(admin);
    // Navigate to the selected admin panel
    window.location.href = admin.route;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Shield className="w-10 h-10 text-indigo-600 mr-4" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Unified Super Admin</h1>
                <p className="text-sm text-gray-600">Choose your preferred admin interface</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-indigo-50 px-4 py-2 rounded-full">
                <Monitor className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-800">All Systems Operational</span>
              </div>
              <button
                onClick={() => window.open('/', '_blank')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg flex items-center font-medium"
              >
                <Globe className="w-5 h-5 mr-2" />
                View Website
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Admin Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the admin interface that best fits your needs. Each panel offers unique advantages 
            for managing your Gifted Solutions eCommerce platform.
          </p>
        </div>

        {/* Admin Panels Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {adminPanels.map((admin) => {
            const IconComponent = admin.icon;
            return (
              <div
                key={admin.id}
                className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${admin.gradient} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-4">
                    <IconComponent className="w-12 h-12" />
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">Active</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{admin.title}</h3>
                  <p className="text-white/90 text-sm leading-relaxed">{admin.description}</p>
                </div>

                {/* Stats */}
                <div className="p-6 border-b border-gray-100">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{admin.stats.storage}</div>
                      <div className="text-xs text-gray-500">Storage</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{admin.stats.performance}</div>
                      <div className="text-xs text-gray-500">Performance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{admin.stats.uptime}</div>
                      <div className="text-xs text-gray-500">Uptime</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900">{admin.stats.locations}</div>
                      <div className="text-xs text-gray-500">Locations</div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="p-6 border-b border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {admin.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div className="p-6 border-b border-gray-100">
                  <h4 className="font-semibold text-gray-900 mb-3">Benefits</h4>
                  <ul className="space-y-2">
                    {admin.benefits.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <Star className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Button */}
                <div className="p-6">
                  <button
                    onClick={() => handleAdminSelect(admin)}
                    className={`w-full bg-gradient-to-r ${admin.gradient} hover:opacity-90 text-white px-6 py-4 rounded-xl font-semibold flex items-center justify-center transition-all duration-200 transform hover:scale-105`}
                  >
                    Access {admin.title}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() => window.open('/supabase-super-admin', '_blank')}
              className="flex items-center justify-center p-4 bg-green-50 hover:bg-green-100 rounded-xl border border-green-200 transition-colors"
            >
              <Database className="w-6 h-6 text-green-600 mr-3" />
              <span className="font-medium text-green-800">Supabase Admin</span>
            </button>
            <button
              onClick={() => window.open('/vercel-super-admin', '_blank')}
              className="flex items-center justify-center p-4 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200 transition-colors"
            >
              <Cloud className="w-6 h-6 text-blue-600 mr-3" />
              <span className="font-medium text-blue-800">Vercel Admin</span>
            </button>
            <button
              onClick={() => window.open('/hybrid-super-admin', '_blank')}
              className="flex items-center justify-center p-4 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-200 transition-colors"
            >
              <Layers className="w-6 h-6 text-purple-600 mr-3" />
              <span className="font-medium text-purple-800">Hybrid Admin</span>
            </button>
            <button
              onClick={() => window.open('/', '_blank')}
              className="flex items-center justify-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl border border-gray-200 transition-colors"
            >
              <Globe className="w-6 h-6 text-gray-600 mr-3" />
              <span className="font-medium text-gray-800">View Website</span>
            </button>
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center mt-12">
          <p className="text-gray-600">
            All admin panels are fully integrated with your Gifted Solutions eCommerce platform.
            <br />
            Choose the interface that best matches your workflow and requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedSuperAdmin;
